import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import Loader from '../../../BaseComponents/Loader';
import TableSkeleton from '../../../BaseComponents/TableSkeleton';
import './TagsTabele.scss';
import TableHeader from '../../../BaseComponents/TableHeader';
import { Tag } from '../../../../types/apiGeneratedTypes';
import { toast } from 'react-toastify';
import TagsTableRow from './TagsTableRow';
import { getTagsList } from '../../../../api/tags';
import { setTagsList } from '../../../../store/appSlice';

const headerColumns = [
  { title: 'Color', key: 'color', minWidth: '100px', flex: 0 },
  { title: 'Name', key: 'name', minWidth: '250px', flex: 0, sortable: true, apiKey: 'name' },
  { title: 'Description', key: 'description', minWidth: '400px', flex: 1 },
  { title: 'Status', key: 'status', minWidth: '120px', flex: 0 },
  { title: '', key: 'actions', minWidth: '50px', flex: 0 },
];

interface TagsTabeleProps {
  onEdit: (tag: Tag) => void;
  onToggleStatus: (tag: Tag) => void;
}

const TagsTabele: React.FC<TagsTabeleProps> = ({ onEdit, onToggleStatus }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [sortingLoading, setSortingLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<any>(null);
  const { tagsList } = useSelector((state: RootState) => state.app);
  const [sortedTags, setSortedTags] = React.useState<Tag[] | null>(null);

  const fetchTags = useCallback(async (search?: string) => {
    try {
      setLoading(true);
      const tagsListResp = await getTagsList(search);
      dispatch(setTagsList(tagsListResp.tags ?? []));
      setSortedTags(null);
    } catch (error) {
      console.error(error);
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  const onSort = async (columnKey: string, direction: 'asc' | 'desc') => {
    try {
      setSortingLoading(true);
      const tagsListResp = await getTagsList('', columnKey, direction);
      setSortedTags(tagsListResp.tags ?? []);
    } catch (err) {
      console.error('Error sorting tags:', err);
      toast.error('Failed to sort tags. Please try again.');
    } finally {
      setSortingLoading(false);
    }
  };

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  if (loading) return <Loader />;
  if (error) return <div>Error loading tags...</div>;

  const displayTags = sortedTags ?? tagsList;

  return (
    <div className='scroll-container'>
      <div className='table-container tags-table'>
        <TableHeader headerColumns={headerColumns} gap={'24px'} onSort={onSort} />
        <div className='table-body'>
          {sortingLoading ? (
            <TableSkeleton rows={5} columns={5} />
          ) : displayTags?.length > 0 ? (
            displayTags.map((tag: Tag) => (
              <TagsTableRow
                key={tag.id}
                tagData={tag}
                onEdit={onEdit}
                onToggleStatus={onToggleStatus}
              />
            ))
          ) : (
            <div className='empty-state'>No tags found.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TagsTabele;
